import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

const ACCENT = "#D4AF37";
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

const PRESETS = [5, 10, 25, 50, 100];

/**
 * Persistent Tip Jar powered by PayPal Smart Buttons.
 * Client-side only — funds flow directly to the merchant PayPal account
 * tied to the configured Client ID.
 */
export default function PayPalTipJar({ isNight = true, recipientLabel = "Caprio" }) {
    const [amount, setAmount] = useState(10);
    const [customAmt, setCustomAmt] = useState("");
    const [status, setStatus] = useState(null); // "success" | "error" | null
    const [payerName, setPayerName] = useState("");
    const [showButtons, setShowButtons] = useState(false);

    const finalAmount = (() => {
        const c = parseFloat(customAmt);
        if (!Number.isNaN(c) && c > 0) return c;
        return amount;
    })();

    if (!CLIENT_ID) {
        return (
            <div
                data-testid="paypal-tip-jar-missing"
                className="rounded-2xl px-6 py-6 text-center text-xs tracking-[0.25em] uppercase"
                style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.6)", border: `1px solid ${ACCENT}55` }}
            >
                PayPal not configured
            </div>
        );
    }

    return (
        <div
            data-testid="paypal-tip-jar"
            className="rounded-2xl p-6 md:p-8"
            style={{
                background: isNight ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                border: `1px solid ${ACCENT}55`,
                backdropFilter: "blur(14px)",
            }}
        >
            <div className="text-[10px] tracking-[0.5em] uppercase mb-2" style={{ color: ACCENT }}>
                Support the Studio
            </div>
            <div className="font-serif-display text-3xl md:text-4xl uppercase tracking-luxe" style={{ color: isNight ? "#fff" : "#0a0a0a" }}>
                Tip {recipientLabel}
            </div>
            <p className="mt-2 text-sm" style={{ color: isNight ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }}>
                Any amount keeps the estate humming. Instant, secure, and stays right here.
            </p>

            <div className="mt-5 flex flex-wrap gap-2" data-testid="tip-presets">
                {PRESETS.map((p) => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => { setAmount(p); setCustomAmt(""); }}
                        data-testid={`tip-preset-${p}`}
                        className="px-4 py-2 rounded-full text-xs tracking-[0.25em] uppercase transition-all"
                        style={{
                            background: (!customAmt && amount === p) ? ACCENT : "transparent",
                            color: (!customAmt && amount === p) ? "#0a0a0a" : (isNight ? "#fff" : "#0a0a0a"),
                            border: `1px solid ${ACCENT}${(!customAmt && amount === p) ? "" : "55"}`,
                        }}
                    >
                        ${p}
                    </button>
                ))}
                <div
                    className="flex items-center gap-1 px-3 rounded-full"
                    style={{ border: `1px solid ${ACCENT}55` }}
                >
                    <span className="text-xs" style={{ color: ACCENT }}>$</span>
                    <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="Custom"
                        value={customAmt}
                        onChange={(e) => setCustomAmt(e.target.value)}
                        data-testid="tip-custom-input"
                        className="bg-transparent outline-none text-sm w-20 py-1.5"
                        style={{ color: isNight ? "#fff" : "#0a0a0a" }}
                    />
                </div>
            </div>

            <div className="mt-6" data-testid="paypal-buttons-wrap">
                {showButtons ? (
                    <PayPalScriptProvider
                        options={{
                            clientId: CLIENT_ID,
                            currency: "USD",
                            intent: "capture",
                            components: "buttons",
                        }}
                    >
                        <PayPalButtons
                            key={finalAmount /* force re-render if amount changes */}
                            style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
                            forceReRender={[finalAmount]}
                            disabled={!finalAmount || finalAmount <= 0}
                            createOrder={(data, actions) => actions.order.create({
                                intent: "CAPTURE",
                                purchase_units: [{
                                    description: `Tip for ${recipientLabel}`,
                                    amount: { currency_code: "USD", value: Number(finalAmount).toFixed(2) },
                                }],
                            })}
                            onApprove={async (data, actions) => {
                                try {
                                    const details = await actions.order.capture();
                                    setPayerName(details?.payer?.name?.given_name || "");
                                    setStatus("success");
                                } catch (e) {
                                    console.error(e);
                                    setStatus("error");
                                }
                            }}
                            onError={(err) => { console.error("PayPal error", err); setStatus("error"); }}
                        />
                    </PayPalScriptProvider>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowButtons(true)}
                        data-testid="tip-continue-paypal"
                        disabled={!finalAmount || finalAmount <= 0}
                        className="w-full py-3.5 rounded-full text-[11px] tracking-[0.35em] uppercase transition-all hover:scale-[1.01] disabled:opacity-40"
                        style={{ background: ACCENT, color: "#0a0a0a", boxShadow: `0 10px 30px -12px ${ACCENT}` }}
                    >
                        Tip ${Number(finalAmount).toFixed(2)} · Continue with PayPal
                    </button>
                )}
            </div>

            {status === "success" && (
                <div
                    data-testid="tip-success"
                    className="mt-4 rounded-lg px-4 py-3 text-sm"
                    style={{ background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}80` }}
                >
                    Thank you{payerName ? `, ${payerName}` : ""}! The estate salutes you.
                </div>
            )}
            {status === "error" && (
                <div
                    data-testid="tip-error"
                    className="mt-4 rounded-lg px-4 py-3 text-sm"
                    style={{ background: "rgba(255,80,80,0.12)", color: "#ff9999", border: "1px solid rgba(255,80,80,0.4)" }}
                >
                    Payment could not be completed. Please try again.
                </div>
            )}
        </div>
    );
}
